import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
// import DeleteUserModal from "components/Users/DeleteUserModal";
// import UpdateUserModal from "components/Users/UpdateUserModal";
import { userApiService } from "services/userApiService";
import DeleteQwikMedicUserModal from "./DeleteQwikMedicUserModal";
import UpdateQwikMedicUserModal from "./UpdateQwikMedicUserModal";
import DeletePromoModal from "components/PromoCode/DeletePromoModal";
import DeleteModal from "pages/Calendar/DeleteModal";

const AllQwikMedicUser = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch users
  const fetchUsers = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await userApiService.getAllUsers();
      setUsers(data.reverse());
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle update user
  const handleUpdateUser = async (formData) => {
    if (!selectedUser) return;

    try {
      await userApiService.updateUser(selectedUser.id, formData);
      toast.success("User updated successfully");
      fetchUsers(false);
      setUpdateModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error("Failed to update user");
      console.error("Error updating user:", error);
      throw error;
    }
  };

  const [deletingOrder, setDeletingOrder] = useState(false);

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setDeletingOrder(true);
    try {
      await userApiService.deleteUser(selectedUser.id);
      toast.success("User deleted successfully");
      fetchUsers(false);
      setDeleteModalOpen(false);
      setSelectedUser(null);
      setDeletingOrder(false);
    } catch (error) {
      setDeletingOrder(false);
      toast.error("Failed to delete user");
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  // Handle edit click
  const handleEditClick = (user) => {
    setSelectedUser(user);
    setUpdateModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  // Handle create user (navigate to create page)
  const handleCreateUser = () => {
    navigate("/create-qwik-medic-user");
  };

  // Filter users based on search and status
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter(user => {
        if (filterStatus === "active") return !user.blockstatus;
        if (filterStatus === "blocked") return user.blockstatus;
        if (filterStatus === "inside_dhaka") return user.insidedhaka;
        if (filterStatus === "outside_dhaka") return !user.insidedhaka;
        return true;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        (user.name && user.name.toLowerCase().includes(query)) ||
        (user.phonenumber && user.phonenumber.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query)) ||
        (user.city && user.city.toLowerCase().includes(query)) ||
        (user.myreferralcode && user.myreferralcode.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [users, searchQuery, filterStatus]);

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => `#${row.original.id}`,
      },
      {
        header: "User Info",
        accessorKey: "user_info",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0 me-3">
                {user.image ? (
                  <img
                    src={user.image}
                    alt=""
                    className="rounded-circle"
                    style={{ width: "40px", height: "40px", objectFit: "cover" }}
                  />
                ) : (
                  <div className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}>
                    <i className="fas fa-user text-muted"></i>
                  </div>
                )}
              </div>
              <div className="flex-grow-1">
                <div className="">{user.name || "N/A"}</div>
                <small className="text-muted">{user.phonenumber}</small>
              </div>
            </div>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>{row.original.email || "N/A"}</div>
        ),
      },
      {
        header: "Gender",
        accessorKey: "gender",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>{row.original.gender || "N/A"}</div>
        ),
      },
      {
        header: "Location",
        accessorKey: "location",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.city || "N/A"}</div>
            <small className="text-muted">
              {row.original.insidedhaka ? (
                <span className="badge bg-success">Inside Dhaka</span>
              ) : (
                <span className="badge bg-warning">Outside Dhaka</span>
              )}
            </small>
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const user = row.original;
          if (user.blockstatus) {
            return (
              <span className="badge bg-danger">
                <i className="fas fa-ban me-1"></i> Blocked
              </span>
            );
          }
          return (
            <span className="badge bg-success">
              <i className="fas fa-check-circle me-1"></i> Active
            </span>
          );
        },
      },
      // {
      //   header: "Balance",
      //   accessorKey: "balance",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div>
      //       <div className="fw-semibold text-primary">
      //         <i className="fas fa-wallet me-1"></i>
      //         QMoney: {row.original.qmoney || "0"}৳
      //       </div>
      //       <small className="text-muted">
      //         QCoins: {row.original.qcoins || "0"}
      //       </small>
      //     </div>
      //   ),
      // },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              onClick={() => handleEditClick(row.original)}
              className="btn btn-primary btn-sm"
              title="Edit"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={() => handleDeleteClick(row.original)}
              className="btn btn-danger btn-sm"
              title="Delete"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Statistics
  const totalUsers = users.length;
  const activeUsers = users.filter(u => !u.blockstatus).length;
  const blockedUsers = users.filter(u => u.blockstatus).length;
  const insideDhakaUsers = users.filter(u => u.insidedhaka).length;
  const totalQMoney = users.reduce((sum, user) => sum + parseFloat(user.qmoney || 0), 0);

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="User Management" breadcrumbItem="All Users" />


        {/* Action Bar */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <div className="input-group" style={{ width: "350px" }}>
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, phone, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchQuery("")}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            <select
              className="form-select"
              style={{ width: "200px" }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="blocked">Blocked Only</option>
              <option value="inside_dhaka">Inside Dhaka</option>
              <option value="outside_dhaka">Outside Dhaka</option>
            </select>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={() => fetchUsers(false)}
              disabled={refreshing}
              title="Refresh"
            >
              <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreateUser}
            >
              <i className="fas fa-plus me-2"></i>
              Create New User
            </button>
          </div>
        </div>

        {/* Users Table */}
        <div className="card">
          <div className="card-body">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-users fa-3x text-muted mb-3"></i>
                <h4>No users found</h4>
                <p className="text-muted mb-4">
                  {searchQuery || filterStatus !== "all"
                    ? 'Try a different search term or filter'
                    : 'Create your first user to get started'}
                </p>
                {(searchQuery || filterStatus !== "all") ? (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterStatus("all");
                    }}
                  >
                    Clear Filters
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateUser}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Create First User
                  </button>
                )}
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredUsers}
                isGlobalFilter={false}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                isCustomPageSize={true}
              />
            )}
          </div>
        </div>

        {/* Modals */}
        <UpdateQwikMedicUserModal
          isOpen={updateModalOpen}
          toggle={() => {
            setUpdateModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSave={handleUpdateUser}
        />

        {/* <DeleteQwikMedicUserModal
          isOpen={deleteModalOpen}
          toggle={() => {
            setDeleteModalOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onDelete={handleDeleteUser}
        /> */}

        <DeleteModal
          show={deleteModalOpen}
          onDeleteClick={handleDeleteUser}
          loader={deletingOrder}
          onCloseClick={() => setDeleteModalOpen(false)}
          title="Delete Order"
          message="Are you sure you want to delete this order? This action cannot be undone."
        />


      </div>
    </div>
  );
};

export default AllQwikMedicUser;